/**
 * Renders plain-text NEET question/option/explanation content safely, with
 * `^{..}`/`^x` and `_{..}`/`_x` converted to real Unicode super/subscript
 * glyphs — so powers (x^2, 10^-3) and chemical formulas (H_2O, CO_2) render
 * correctly wherever a question is shown (live test, result review, admin
 * preview). Content is escaped first since it's authored as plain text, not
 * markup, then the result is meant for dangerouslySetInnerHTML.
 */

const SUPER_MAP = {
  0: '⁰', 1: '¹', 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹',
  '+': '⁺', '-': '⁻', '=': '⁼', '(': '⁽', ')': '⁾', n: 'ⁿ',
};

const SUB_MAP = {
  0: '₀', 1: '₁', 2: '₂', 3: '₃', 4: '₄', 5: '₅', 6: '₆', 7: '₇', 8: '₈', 9: '₉',
  '+': '₊', '-': '₋', '=': '₌', '(': '₍', ')': '₎',
};

const mapEach = (str, map) =>
  String(str).split('').map((ch) => map[ch] ?? ch).join('');

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export const formatScientificText = (input) => {
  if (input == null) return '';
  // Common ASCII math shorthand → proper symbol (before escaping, since it's
  // matched on raw `<`/`>` sequences rather than HTML entities).
  let s = String(input)
    .replace(/<=/g, '≤')
    .replace(/>=/g, '≥')
    .replace(/!=/g, '≠')
    .replace(/\+-|-\+/g, '±')
    .replace(/->/g, '→')
    .replace(/<-/g, '←');

  s = escapeHtml(s);

  // `^{...}`/`_{...}` for anything explicit; otherwise `^`/`_` followed by a
  // signed run of digits (T^-1, 10^-19, Ca^2+, H_2, O_2-), a bare sign with
  // an implicit coefficient of 1 (Na^+, Cl^-, as ions are conventionally
  // written), or a single letter (x^n). A lone single-char capture would
  // leave e.g. `^-1` as "⁻1" (only the sign superscripted), so the digit run
  // has to be captured together with its sign.
  const EXPONENT_TOKEN = /(-?\d+[+-]?|[+-]|[A-Za-z])/;
  s = s.replace(/\^\{([^{}]+)\}/g, (_, g) => mapEach(g, SUPER_MAP));
  s = s.replace(new RegExp('\\^' + EXPONENT_TOKEN.source, 'g'), (_, g) => mapEach(g, SUPER_MAP));
  s = s.replace(/_\{([^{}]+)\}/g, (_, g) => mapEach(g, SUB_MAP));
  s = s.replace(new RegExp('_' + EXPONENT_TOKEN.source, 'g'), (_, g) => mapEach(g, SUB_MAP));

  return s.replace(/\n/g, '<br />');
};

export default formatScientificText;
